import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/Layout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, ShieldCheck, HardHat } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Team() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "crew", phone: "" });

  const load = () => api.listUsers().then(setUsers).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Name, email and password required");
    try { await api.createUser(form); toast.success("Team member added"); setOpen(false); setForm({ name: "", email: "", password: "", role: "crew", phone: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (u) => { if (!window.confirm(`Remove ${u.name}?`)) return; try { await api.deleteUser(u.id); toast.success("Removed"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  return (
    <div className="p-8 max-w-[1400px]">
      <PageHeader title="Team" subtitle="Office admins & field crew" actions={
        <Button onClick={() => setOpen(true)} className="bg-[#0B1726] hover:bg-slate-800" data-testid="add-user-button"><Plus size={18} className="mr-1" /> Add Member</Button>
      } />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.map((u) => (
          <div key={u.id} className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm flex items-center gap-4 group" data-testid={`user-card-${u.id}`}>
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-lg font-bold text-slate-500 overflow-hidden shrink-0">
              {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-full h-full object-cover" /> : u.name[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 truncate">{u.name}</div>
              <div className="text-xs text-slate-500 truncate">{u.email}</div>
              <span className={`inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${u.role === "admin" ? "bg-[#0B1726] text-white" : "bg-cyan-50 text-cyan-700 border border-cyan-200"}`}>
                {u.role === "admin" ? <ShieldCheck size={11} weight="fill" /> : <HardHat size={11} weight="fill" />} {u.role}
              </span>
            </div>
            <button onClick={() => remove(u)} className="text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-all duration-200" data-testid={`delete-user-${u.id}`}><Trash size={17} /></button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Add Team Member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Full Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="user-name-input" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="user-email-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Password</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" data-testid="user-password-input" /></div>
              <div><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="crew">Field Crew</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#0B1726] hover:bg-slate-800" data-testid="save-user-button">Add Member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
